import type { SystemStyleObject } from '@chakra-ui/styled-system';

/** Sticky `Thead` should sit above the copy feedback bubble. */
export const STICKY_TABLE_HEADER_Z_INDEX = 11;
/**
 * Draggable bulk-action / save bar. Portaled to `document.body`, so this
 * must beat the app shell (`main` is 20), the sticky thead (11–12), and
 * Order Detail's sidebar toggle (20). Stays below Chakra popovers/modals.
 */
export const FLOATING_ACTION_BAR_Z_INDEX = 100;
/** Default bubble stacking when not covered by a sticky header. */
export const COPY_TOOLTIP_Z_INDEX = 10;
/** Portalled copy feedback — above Chakra modals (1400) and tooltips (1500). */
export const COPY_FEEDBACK_Z_INDEX = 1600;

/** Marker on `CopyTooltip` wrappers — used by table CSS to allow overflow. */
export const COPY_ANCHOR_ATTR = 'data-copy-anchor';
/** Present while the "Copied!" bubble is visible — lifts the row above neighbors. */
export const COPY_ACTIVE_ATTR = 'data-copy-active';
/** The feedback bubble element — hidden via CSS when the table has scrolled. */
export const COPY_BUBBLE_ATTR = 'data-copy-bubble';

/** Sticky corner header (first column) sits above the rest of the thead. */
export const STICKY_TABLE_CORNER_Z_INDEX = STICKY_TABLE_HEADER_Z_INDEX + 1;
/** Modal toolbars with filter dropdowns must sit above sticky table headers. */
export const FILTER_MENU_Z_INDEX = STICKY_TABLE_HEADER_Z_INDEX + 1;

/**
 * Scroll-container overrides so sticky `thead` stacks above copy bubbles (11 > 10).
 * Spread into the `sx` of `overflow: auto` table wrappers (Home, Dashboard, etc.).
 */
export const stickyScrollTableHeadSx: SystemStyleObject = {
    '& thead': {
        zIndex: `${STICKY_TABLE_HEADER_Z_INDEX} !important`,
    },
    '& th:first-of-type': {
        zIndex: `${STICKY_TABLE_CORNER_Z_INDEX} !important`,
    },
};

/** Set on a scroll container while at scroll top (or when the table cannot scroll). */
export const STICKY_TABLE_COPY_FRONT_ATTR = 'data-sticky-table-copy-front';

/** First data row copy feedback stacks above the sticky header in front mode. */
export const STICKY_TABLE_FIRST_ROW_COPY_Z_INDEX = STICKY_TABLE_CORNER_Z_INDEX + 1;

/**
 * Lift any row with an active copy bubble above sticky columns / adjacent rows
 * while at scroll top. The bubble extends upward and can cross into a previous
 * row's sticky first column on hover; this z-index puts the active row above
 * all sticky cells of other rows. Cleared when the table scrolls (the hook also
 * clears the bubble itself), so the lift never fights the sticky header.
 */
const stickyTableActiveRowSelector =
    `&[${STICKY_TABLE_COPY_FRONT_ATTR}="true"] tbody tr:has([${COPY_ACTIVE_ATTR}])`;

/**
 * Sticky header tables inside a scroll container: header above copy when scrolled,
 * active-copy row lifted above sticky columns at scroll top (see
 * `useStickyTableCopyFront`).
 */
export const stickyTableScrollCopySx: SystemStyleObject = {
    ...stickyScrollTableHeadSx,
    [stickyTableActiveRowSelector]: {
        position: 'relative',
        zIndex: STICKY_TABLE_FIRST_ROW_COPY_Z_INDEX,
    },
};

/** @deprecated Use {@link STICKY_TABLE_COPY_FRONT_ATTR} */
export const HOME_COPY_FRONT_ATTR = STICKY_TABLE_COPY_FRONT_ATTR;

/** @deprecated Use {@link stickyTableScrollCopySx} */
export const homeScrollContainerCopySx = stickyTableScrollCopySx;

const copyAnchorSelector = `& td:has([${COPY_ANCHOR_ATTR}])`;
const copyActiveRowSelector = `& tr:has([${COPY_ACTIVE_ATTR}])`;

/** Let copy tooltips escape clipped cells; raise the active row in non-sticky tables. */
export const copyOverflowTableSx: SystemStyleObject = {
    [copyAnchorSelector]: {
        overflow: 'visible',
    },
    [copyActiveRowSelector]: {
        position: 'relative',
        zIndex: 2,
    },
};
